import { DigitalTwinsClient } from '@azure/digital-twins-core'
import type {
  PipelineRequest,
  PipelineResponse,
  SendRequest,
  PipelinePolicy
} from '@azure/core-rest-pipeline'
import type {
  ContainerClient,
  RequestPolicy,
  RequestPolicyFactory,
  WebResource
} from '@azure/storage-blob'
import { BlobServiceClient, newPipeline } from '@azure/storage-blob'
import {
  CloudSettings,
  Client as KustoClient,
  KustoConnectionStringBuilder,
  kustoTrustedEndpoints,
  MatchRule
} from 'azure-kusto-data'
import { tokenCredential, tokenProvider } from './auth'

/**
 * Pipeline policy that rewrites URLs and adds custom headers (core-rest-pipeline)
 */
const createCustomProxyPolicy = (
  environmentId: string,
  pathRewrite: (path: string) => string
): PipelinePolicy => {
  return {
    name: 'customProxyPolicy',
    sendRequest: async (request: PipelineRequest, next: SendRequest): Promise<PipelineResponse> => {
      // Add custom header
      request.headers.set('x-adt-id', environmentId)

      // Rewrite URL
      const url = new URL(request.url)
      const baseUrl = new URL(globalThis.location.origin)
      url.host = baseUrl.host
      url.pathname = pathRewrite(url.pathname)
      url.protocol = baseUrl.protocol
      request.url = url.toString()

      // Pass to next policy in pipeline
      return next(request)
    }
  }
}

/**
 * Custom RequestPolicyFactory for storage blob clients
 */
class CustomStorageRequestPolicyFactory implements RequestPolicyFactory {
  private readonly _environmentId: string
  private readonly _pathRewrite: (path: string) => string

  constructor(environmentId: string, pathRewrite: (path: string) => string) {
    this._environmentId = environmentId
    this._pathRewrite = pathRewrite
  }

  public create(nextPolicy: RequestPolicy): RequestPolicy {
    return {
      sendRequest: async (request: WebResource) => {
        // Add custom header
        request.headers.set('x-adt-id', this._environmentId)

        // Rewrite URL
        const url = new URL(request.url)
        const baseUrl = new URL(globalThis.location.origin)
        url.host = baseUrl.host
        url.pathname = this._pathRewrite(url.pathname)
        url.protocol = baseUrl.protocol
        request.url = url.toString()

        // Pass to next policy in pipeline
        return nextPolicy.sendRequest(request)
      }
    }
  }
}

/**
 * Custom Bearer Token Authentication Policy Factory for storage blob clients
 *
 * Note: tokenCredential wraps getApiToken() internally (returns B2C token regardless
 * of requested scope), so this policy is also used for the OneLake proxy which
 * requires the B2C token.
 */
class BearerTokenAuthenticationPolicyFactory implements RequestPolicyFactory {
  public create(nextPolicy: RequestPolicy): RequestPolicy {
    return {
      sendRequest: async (request: WebResource) => {
        // Get token from tokenCredential
        const token = await tokenCredential.getToken(['https://storage.azure.com/.default'])
        if (token) {
          request.headers.set('Authorization', `Bearer ${token.token}`)
        }

        // Pass to next policy in pipeline
        return nextPolicy.sendRequest(request)
      }
    }
  }
}
const bearerTokenPolicyFactory = new BearerTokenAuthenticationPolicyFactory()

/** Digital Twins Clients cache object */
const digitalTwinsClients: { [environmentId: string]: DigitalTwinsClient } = {}

/** Digital Twins client factory that returns cached Digital Twins Client */
export const digitalTwinsClientFactory = (
  environmentId: string,
  adtHost: string
): DigitalTwinsClient | null => {
  // `/api/digitaltwins${url.pathname}`
  if (!digitalTwinsClients[environmentId]) {
    // find all consecutive forward slashes (two or more)
    const _pathRegex = /(\/){2,}/g
    const _pathRewrite = (path: string) =>
      `${import.meta.env.twinsProxy}${path}`.replace(_pathRegex, '/')

    const customPolicy = createCustomProxyPolicy(environmentId, _pathRewrite)

    digitalTwinsClients[environmentId] = new DigitalTwinsClient(
      `https://${adtHost}/`,
      tokenCredential,
      {
        allowInsecureConnection: globalThis.location.hostname === 'localhost',
        additionalPolicies: [{ policy: customPolicy, position: 'perCall' }]
      }
    )
  }
  return digitalTwinsClients[environmentId]
}

/** Blob Service Clients cache object */
const blobContainerClients: { [environmentId: string]: ContainerClient } = {}

/** Blob Service client factory that returns cached Blob Service Client */
export const blobContainerClientFactory = (
  environmentId: string,
  storageHost: string,
  storageContainer = 'digitaltwins'
): ContainerClient => {
  if (!blobContainerClients[environmentId]) {
    // Container is removed from path (and added again in backend proxy)
    // find all consecutive forward slashes (two or more)
    const _pathRegex = /(\/){2,}/g
    const _pathRewrite = (path: string) =>
      `${import.meta.env.storageProxy}${path?.replace(storageContainer, '')}`.replace(
        _pathRegex,
        '/'
      )

    const customPolicyFactory = new CustomStorageRequestPolicyFactory(environmentId, _pathRewrite)

    // Create anonymous pipeline to avoid automatic bearer token policy TLS check on localhost
    const pipeline = newPipeline()
    pipeline.factories.push(customPolicyFactory, bearerTokenPolicyFactory)

    const serviceClient = new BlobServiceClient(`https://${storageHost}/`, pipeline)
    blobContainerClients[environmentId] = serviceClient.getContainerClient(storageContainer)
  }
  return blobContainerClients[environmentId]
}

/** Kusto add trusted endpoints */
kustoTrustedEndpoints.overrideMatcher = (host: string) => host?.includes('.arcadis')
kustoTrustedEndpoints.addTrustedHosts([new MatchRule('.arcadis.com', false)], true)

/** Kusto Clients cache object */
const kustoClients: { [environmentId: string]: KustoClient } = {}

/** Kusto client factory that returns cached Kusto Client */
export const kustoClientFactory = (environmentId: string) => {
  if (!kustoClients[environmentId]) {
    const clusterUrl = `${globalThis.location.origin}${import.meta.env.adxProxy}${environmentId}`
    // Avoids that kusto library tries to retrieve auth metadata
    CloudSettings.writeToCache(clusterUrl)
    // Create Kusto connection string builder with custom B2C token provider
    const kcsb = KustoConnectionStringBuilder.withTokenProvider(clusterUrl, tokenProvider)
    // Create and cache kusto client
    kustoClients[environmentId] = new KustoClient(kcsb)
  }
  return kustoClients[environmentId]
}

const envBlobContainerClients: { [environmentId: string]: ContainerClient } = {}
/** Blob Service client factory that returns cached Blob Service Client */
export const envBlobContainerClientFactory = (
  environmentId: string,
  storageContainer = 'images'
): ContainerClient => {
  if (!envBlobContainerClients[environmentId]) {
    // This will both replace the host and path
    const _pathRewrite = (path: string) => path

    const customPolicyFactory = new CustomStorageRequestPolicyFactory(environmentId, _pathRewrite)

    // Create anonymous pipeline to avoid automatic bearer token policy
    const pipeline = newPipeline()
    pipeline.factories.push(customPolicyFactory, bearerTokenPolicyFactory)

    // Host doesn't matter, it will be overwritten by the custom policy
    const baseUrl = new URL(globalThis.location.origin)
    const serviceClient = new BlobServiceClient(
      `https://${baseUrl.host}${import.meta.env.environmentsApi}${environmentId}`,
      pipeline
    )
    envBlobContainerClients[environmentId] = serviceClient.getContainerClient(storageContainer)
  }
  return envBlobContainerClients[environmentId]
}

/** Shared Blob Client cache (keyed by environmentId for cache lifetime management) */
const sharedBlobClients: { [environmentId: string]: ContainerClient } = {}

/**
 * Returns a ContainerClient for the env-files container on the shared storage
 * account (stdtmain{env}), routing through `/api/storage/env-files/` on the proxy.
 * Blobs under the `shared/` prefix are read-only shared blobs resolvable by sharedBlobId.
 */
export const sharedBlobContainerClientFactory = (environmentId: string): ContainerClient => {
  if (!sharedBlobClients[environmentId]) {
    const _pathRewrite = (path: string) =>
      `${import.meta.env.storageProxy}env-files/${path.replace(/^.*?env-files\//, '')}`

    const customPolicyFactory = new CustomStorageRequestPolicyFactory(environmentId, _pathRewrite)

    const pipeline = newPipeline()
    pipeline.factories.push(customPolicyFactory, bearerTokenPolicyFactory)

    // Host doesn't matter — the proxy policy rewrites it to the origin
    const baseUrl = new URL(globalThis.location.origin)
    const serviceClient = new BlobServiceClient(`https://${baseUrl.host}/dummy-account`, pipeline)
    sharedBlobClients[environmentId] = serviceClient.getContainerClient('env-files')
  }
  return sharedBlobClients[environmentId]
}
